import type { Request, Touchpoint } from '../types';

/**
 * API Response Transformers
 *
 * Transforms backend API responses (with old field names) to the corrected
 * domain model types (with new field names).
 *
 * This is the single point of transformation - after calling these functions,
 * the entire app uses the corrected field names consistently.
 */

// ============================================================================
// Request Transformation
// ============================================================================

/**
 * Transform "all-ondemand-global-requests" API response to Request types.
 *
 * Maps old field names from backend to new corrected field names.
 *
 * @param apiData - Raw API response data array
 * @returns Typed Request array with corrected field names
 */
export function transformRequestResponse(apiData: any[]): Request[] {
  return apiData.map(raw => ({
    datasetType: 'request',

    // Account fields
    acc_name: raw.account || '',

    // Opportunity fields
    opp_id: raw.id || '',
    opp_gnarr_grand_total: parseFloat(raw.gnarr) || 0, // using raw.gnarr => project.parameterValues.DE:TPS Deal Grand Total GNARR
    // opp_gnarr: parseFloat(raw.opp_gnarr) || 0,
    opp_gnarr: parseFloat(raw.opp_gnarr) || 0, // using raw.opp_gnarr => project.parameterValues.DE:TPS Deal Grand Total GNARR
    opp_status: raw.opp_status || '',
    opp_adjusted_commit: raw.adjusted_commit || '',
    opp_close_q: raw.close_q || '',
    opp_region: raw.region || '',
    opp_segment: raw.segment || undefined,
    opp_segment_sales: raw.segment_sales || undefined,
    opp_market_area: raw.market_area || undefined,

    // Request fields
    req_id: raw.requestID || '',
    req_name: raw.requestName || '',
    req_age: raw.age !== null && raw.age !== undefined ? parseInt(raw.age, 10) : null,
    req_status: raw.status || 'In Progress',
    req_entry: raw.entry || '',
    req_completion: raw.completion || '',
    req_created_fy: raw.created_fy || '',
    req_created_q: raw.created_q || '',
    req_time: raw.request_time || undefined,
    req_offering: raw.offering || null,
    req_products: raw.products || '',
    req_market: raw.market || null,
    req_sub_region: raw.sub_region || null,
    req_sv_region: raw.sv_region || null,

    // Solution Consultant (User) fields
    user_sc_name: raw.sc || '',
  }));
}

// ============================================================================
// Touchpoint Transformation
// ============================================================================

/**
 * Transform "all-ondemand-global-touchpoints" API response to Touchpoint types.
 *
 * Maps old field names from backend to new corrected field names.
 *
 * @param apiData - Raw API response data array
 * @returns Typed Touchpoint array with corrected field names
 */
export function transformTouchpointResponse(apiData: any[]): Touchpoint[] {
  return apiData.map(raw => ({
    datasetType: 'touchpoint',

    // Account fields
    acc_name: raw.account || '',

    // Opportunity fields
    opp_id: raw.id || '',
    opp_age: raw.age !== null && raw.age !== undefined ? parseInt(raw.age, 10) : null,
    opp_gnarr_grand_total: parseFloat(raw.gnarr) || 0,
    opp_gnarr: parseFloat(raw.opp_gnarr) || 0,
    opp_close_q: raw.close_q || '',
    opp_region: raw.region || '',
    opp_segment: raw.segment || undefined,
    opp_segment_sales: raw.segment_sales || undefined,
    opp_market_area: raw.market_area || undefined,

    // Touchpoint fields
    tp_id: raw.task_id || '',
    tp_name: raw.name || '',
    tp_type: raw.touchpointType || '',
    tp_status: raw.status || '',
    tp_entry: raw.entry || '',
    tp_completion: raw.completion || '',
    tp_actual_start_date: raw.actualStartDate || '',
    tp_created_fy: raw.created_fy || '',
    tp_created_q: raw.created_q || '',
    tp_solution_comments: raw.solutionComments || '',
    tp_comment_text: raw.commentText || '',

    // Solution Consultant (User) fields
    user_sc_name: raw.sc || '',
  }));
}

// ============================================================================
// CSV Upload Transformation (Untracked Data)
// ============================================================================

/**
 * Transform uploaded CSV row to Request type.
 *
 * Used for manually uploaded "untracked" requests that don't come from Workfront.
 * CSV column names should match the new field names after refactoring.
 *
 * @param csvRow - Parsed CSV row object
 * @returns Typed Request object
 */
export function transformCsvRowToRequest(csvRow: Record<string, string>): Request {
  return {
    datasetType: 'request',

    // Account fields
    acc_name: csvRow.acc_name || '',

    // Opportunity fields
    opp_id: csvRow.opp_id || '',
    opp_gnarr_grand_total: parseFloat(csvRow.opp_gnarr_grand_total) || 0,
    opp_gnarr: parseFloat(csvRow.opp_gnarr) || 0,
    opp_status: csvRow.opp_status || '',
    opp_adjusted_commit: csvRow.opp_adjusted_commit || '',
    opp_close_q: csvRow.opp_close_q || '',
    opp_region: csvRow.opp_region || '',
    opp_segment: csvRow.opp_segment || undefined,
    opp_segment_sales: csvRow.opp_segment_sales || undefined,
    opp_market_area: csvRow.opp_market_area || undefined,

    // Request fields
    req_id: csvRow.req_id || '',
    req_name: csvRow.req_name || '',
    req_age: null,  // Will be computed from req_entry/req_completion
    req_status: csvRow.req_status || 'In Progress',
    req_entry: csvRow.req_entry || '',
    req_completion: csvRow.req_completion || '',
    req_created_fy: csvRow.req_created_fy || '',
    req_created_q: csvRow.req_created_q || '',
    req_time: csvRow.req_time || undefined,
    req_offering: csvRow.req_offering || null,
    req_products: csvRow.req_products || '',
    req_market: csvRow.req_market || null,
    req_sub_region: csvRow.req_sub_region || null,
    req_sv_region: csvRow.req_sv_region || null,

    // Solution Consultant (User) fields
    user_sc_name: csvRow.user_sc_name || '',
  };
}
